import { useMemo, useState } from 'react';
import {
  SetTeamAiModelRequest,
  TEAM_AI_PROVIDERS,
  TeamAiEffort,
  TeamAiProvider,
  type AdminProviderModel,
  type AdminProviderModels,
  type AdminTeamAiModel,
  type AdminTeamAiModels,
} from '@boardflight/shared';
import { ApiError, api, post, put } from '../api.js';
import { Async, Modal, Panel, Time, useAsync } from '../ui.js';

/**
 * Team AI's model list (`docs/admin-plan.md` §D5 row 13, `docs/playground-team-ai-plan.md` §6).
 *
 * The list every team's playground AI chooses from: a team admin switches models off on the Team
 * page, and nothing outside this list can be chosen. A change here reaches a running playground the
 * next time it asks for its key, with no deploy.
 *
 * The second panel reads a provider's own catalogue so a model is picked by the id the provider
 * gives it, not typed. That read is the admin Worker's, never this page's: a cross-site request
 * from here would name this origin to the provider (§D4). Cheaper Inference's catalogue needs a key,
 * which goes to the Worker in that one request's body and is kept nowhere — the field is emptied as
 * soon as it is sent.
 */
export function AiModels() {
  const state = useAsync(() => api<AdminTeamAiModels>('/v1/admin/ai-models'), []);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const items = state.data?.items ?? [];

  async function write(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      state.reload();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  function move(index: number, by: -1 | 1) {
    const order = items.map((model) => model.id);
    const [moved] = order.splice(index, 1);
    order.splice(index + by, 0, moved!);
    void write(() => put('/v1/admin/ai-models', { order }));
  }

  function remove(model: AdminTeamAiModel) {
    const lines = [`Take ${model.label} off the list for every team?`];
    if (model.teamsOn > 0) lines.push(`${teams(model.teamsOn)} have it on now.`);
    if (model.teamsOnlyOn > 0) {
      lines.push(
        `For ${teams(model.teamsOnlyOn)} it is the only model on: their playgrounds go back to each developer’s ChatGPT until an admin there switches another on.`,
      );
    }
    if (!window.confirm(lines.join('\n\n'))) return;
    void write(() => api(`/v1/admin/ai-models/${model.id}`, { method: 'DELETE' }));
  }

  return (
    <>
      <Panel title="AI models">
        <p className="muted small">
          What every team’s playgrounds may run on. The first model a provider serves is the default for teams on that
          provider. Teams can switch models off for themselves; they cannot add one.
        </p>
        {error ? <p className="error">{error}</p> : null}
        <Async state={state}>
          {() => (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Model</th>
                    <th>OpenRouter</th>
                    <th>Cheaper Inference</th>
                    <th className="num">Context · compacts at</th>
                    <th>Efforts</th>
                    <th className="num">Teams on</th>
                    <th>Verified</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((model, index) => (
                    <tr key={model.id}>
                      <td>
                        <div className="row">
                          <button
                            type="button"
                            aria-label={`Move ${model.label} up`}
                            disabled={busy || index === 0}
                            onClick={() => move(index, -1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${model.label} down`}
                            disabled={busy || index === items.length - 1}
                            onClick={() => move(index, 1)}
                          >
                            ↓
                          </button>
                        </div>
                      </td>
                      <td>
                        <div>{model.label}</div>
                        <div className="mono muted nowrap">{model.id}</div>
                      </td>
                      <td className="mono nowrap">{model.ids.openrouter ?? <span className="muted">—</span>}</td>
                      <td className="mono nowrap">{model.ids.cheaperinference ?? <span className="muted">—</span>}</td>
                      <td className="num">
                        {tokens(model.contextWindow)} · {tokens(model.autoCompactTokenLimit)}
                      </td>
                      <td>
                        {model.efforts.length === 0 ? (
                          <span className="muted">none</span>
                        ) : (
                          model.efforts.map((effort, at) => (
                            <span key={effort}>
                              {at > 0 ? ', ' : ''}
                              {effort === model.defaultEffort ? <strong>{effort}</strong> : effort}
                            </span>
                          ))
                        )}
                      </td>
                      <td className="num">
                        {model.teamsOn}
                        {model.teamsOnlyOn > 0 ? (
                          <div className="small warn" title="Teams for which this is the only model switched on">
                            {model.teamsOnlyOn} only
                          </div>
                        ) : null}
                      </td>
                      <td>{model.verifiedAt !== null ? <Time at={model.verifiedAt} /> : <span className="muted">provisional</span>}</td>
                      <td>
                        <div className="row">
                          <button type="button" disabled={busy} onClick={() => setEditing(draftOf(model))}>
                            Edit
                          </button>
                          <button type="button" className="danger" disabled={busy} onClick={() => remove(model)}>
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </Panel>

      <ModelUsage />

      <ProviderCatalogue models={items} onPick={setEditing} />

      {editing ? (
        <Editor
          draft={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            state.reload();
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------------------- usage */

interface ModelUsageRow {
  model: string | null;
  credential: string | null;
  turns: number;
  failed: number;
  interrupted: number;
  inputTokens: number;
  outputTokens: number;
  people: number;
  lastAt: number;
}

const CREDENTIALS: Record<string, string> = { team: 'team key', chatgpt: 'ChatGPT' };

/** What the playgrounds ran on, per model and per credential — every model, on the list or not. */
function ModelUsage() {
  const [days, setDays] = useState('30');
  const state = useAsync(
    () => api<{ items: ModelUsageRow[]; days: number }>(`/v1/admin/ai-models/usage?days=${days}`),
    [days],
  );
  return (
    <Panel
      title="Usage"
      actions={
        <select value={days} onChange={(e) => setDays(e.target.value)} aria-label="Range">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      }
    >
      <Async state={state} empty="No playground AI turns in this range.">
        {(data) => {
          if (data.items.length === 0) return <p className="muted">No playground AI turns in this range.</p>;
          const total = data.items.reduce((sum, row) => sum + row.turns, 0);
          return (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Paid by</th>
                    <th className="num">Turns</th>
                    <th className="num">Share</th>
                    <th className="num">Failed</th>
                    <th className="num">Interrupted</th>
                    <th className="num">Tokens in</th>
                    <th className="num">Tokens out</th>
                    <th className="num">People</th>
                    <th>Last</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={`${row.model ?? ''}/${row.credential ?? ''}`}>
                      <td className="mono">{row.model ?? <span className="muted">not reported</span>}</td>
                      <td className="muted">{row.credential ? (CREDENTIALS[row.credential] ?? row.credential) : '—'}</td>
                      <td className="num">{row.turns}</td>
                      <td className="num muted">{Math.round((row.turns / total) * 100)}%</td>
                      <td className={row.failed ? 'num error' : 'num'}>
                        {row.failed ? `${row.failed} · ${Math.round((row.failed / row.turns) * 100)}%` : ''}
                      </td>
                      <td className="num muted">{row.interrupted || ''}</td>
                      <td className="num">{tokens(row.inputTokens)}</td>
                      <td className="num">{tokens(row.outputTokens)}</td>
                      <td className="num">{row.people}</td>
                      <td className="muted small"><Time at={row.lastAt} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }}
      </Async>
    </Panel>
  );
}

/* ------------------------------------------------------------------- a provider's catalogue */

/** How many rows of a catalogue are drawn at once. OpenRouter lists hundreds; a filter finds the rest. */
const SHOWN = 200;

function ProviderCatalogue({
  models,
  onPick,
}: {
  models: readonly AdminTeamAiModel[];
  onPick: (draft: Draft) => void;
}) {
  const [provider, setProvider] = useState<TeamAiProvider>('openrouter');
  const [key, setKey] = useState('');
  const [loaded, setLoaded] = useState<AdminProviderModels | null>(null);
  const [filter, setFilter] = useState('');
  const [toolsOnly, setToolsOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const body = provider === 'cheaperinference' ? { provider, key } : { provider };
    // Emptied as it is sent: the page keeps no copy, and a second load asks for the key again.
    setKey('');
    try {
      setLoaded(await post<AdminProviderModels>('/v1/admin/ai-provider-models', body));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const matching = useMemo(() => {
    if (!loaded) return [];
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return loaded.items.filter(
      (item) =>
        !(toolsOnly && item.tools === false) &&
        words.every((word) => item.id.toLowerCase().includes(word) || (item.name ?? '').toLowerCase().includes(word)),
    );
  }, [loaded, filter, toolsOnly]);

  return (
    <Panel title="Add from a provider">
      <form className="row" onSubmit={(event) => void load(event)}>
        <select
          value={provider}
          aria-label="Provider"
          onChange={(event) => {
            setProvider(event.target.value as TeamAiProvider);
            setLoaded(null);
            setKey('');
          }}
        >
          {TeamAiProvider.options.map((one) => (
            <option key={one} value={one}>
              {TEAM_AI_PROVIDERS[one].label}
            </option>
          ))}
        </select>
        {provider === 'cheaperinference' ? (
          <input
            type="password"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder="Cheaper Inference key"
            aria-label="Cheaper Inference key"
            autoComplete="off"
            spellCheck={false}
            required
          />
        ) : null}
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Loading…' : 'Load models'}
        </button>
      </form>
      <p className="note">
        {provider === 'openrouter'
          ? 'OpenRouter’s list is public, so no key is sent.'
          : 'The key goes to the admin Worker for this one request, is used to ask Cheaper Inference, and is kept nowhere — not in this page either.'}
      </p>
      {error ? <p className="error">{error}</p> : null}

      {loaded && loaded.provider === provider ? (
        <>
          <div className="row">
            <input
              type="text"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter by id or name"
              aria-label="Filter models"
            />
            {loaded.items.some((item) => item.tools !== null) ? (
              <label className="row small">
                <input type="checkbox" checked={toolsOnly} onChange={(event) => setToolsOnly(event.target.checked)} />
                Only models that take tools
              </label>
            ) : null}
            <span className="muted small">
              {matching.length} of {loaded.items.length}
              {matching.length > SHOWN ? `, first ${SHOWN} shown` : ''}
            </span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{TEAM_AI_PROVIDERS[loaded.provider].label} id</th>
                  <th>Name</th>
                  <th className="num">Context</th>
                  <th>Takes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {matching.slice(0, SHOWN).map((item) => (
                  <CatalogueRow key={item.id} provider={loaded.provider} item={item} models={models} onPick={onPick} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Panel>
  );
}

function CatalogueRow({
  provider,
  item,
  models,
  onPick,
}: {
  provider: TeamAiProvider;
  item: AdminProviderModel;
  models: readonly AdminTeamAiModel[];
  onPick: (draft: Draft) => void;
}) {
  const listed = models.find((model) => model.ids[provider] === item.id);
  // The same model under the other provider's id: `openai/gpt-6.1-sol` and `gpt-6.1-sol`.
  const sibling = listed ? undefined : models.find((model) => model.id === idFrom(item.id) && model.ids[provider] === undefined);
  const takes = [item.tools ? 'tools' : null, item.reasoning ? 'reasoning' : null].filter(Boolean).join(', ');

  return (
    <tr>
      <td className="mono nowrap">{item.id}</td>
      <td>{item.name ?? <span className="muted">—</span>}</td>
      <td className="num">{item.contextLength !== null ? tokens(item.contextLength) : <span className="muted">—</span>}</td>
      <td>{item.tools === null && item.reasoning === null ? <span className="muted">not said</span> : takes || 'neither'}</td>
      <td>
        {listed ? (
          <span className="muted small">On the list as {listed.label}</span>
        ) : sibling ? (
          <button type="button" onClick={() => onPick({ ...draftOf(sibling), [provider]: item.id })}>
            Add to {sibling.label}
          </button>
        ) : (
          <button type="button" onClick={() => onPick(draftFrom(provider, item))}>
            Add
          </button>
        )}
      </td>
    </tr>
  );
}

/* --------------------------------------------------------------------------------- editor */

/** A model as the editor holds it: every field as typed, so a half-typed number is not lost. */
interface Draft {
  /** Already on the list: its id is fixed, because teams' switched-off lists name it. */
  existing: boolean;
  id: string;
  label: string;
  openrouter: string;
  cheaperinference: string;
  contextWindow: string;
  autoCompactTokenLimit: string;
  efforts: TeamAiEffort[];
  defaultEffort: TeamAiEffort | '';
  verified: boolean;
}

function draftOf(model: AdminTeamAiModel): Draft {
  return {
    existing: true,
    id: model.id,
    label: model.label,
    openrouter: model.ids.openrouter ?? '',
    cheaperinference: model.ids.cheaperinference ?? '',
    contextWindow: String(model.contextWindow),
    autoCompactTokenLimit: String(model.autoCompactTokenLimit),
    efforts: [...model.efforts],
    defaultEffort: model.defaultEffort ?? '',
    verified: model.verifiedAt !== null,
  };
}

/**
 * A new model from a provider's catalogue entry. What the provider does not say is a guess to check:
 * the compaction limit is the one the shipped open models use, and efforts are low to high unless
 * the provider says the model takes no reasoning.
 */
function draftFrom(provider: TeamAiProvider, item: AdminProviderModel): Draft {
  const window = item.contextLength;
  const efforts: TeamAiEffort[] = item.reasoning === false ? [] : ['low', 'medium', 'high'];
  return {
    existing: false,
    id: idFrom(item.id),
    label: labelFrom(item),
    openrouter: provider === 'openrouter' ? item.id : '',
    cheaperinference: provider === 'cheaperinference' ? item.id : '',
    contextWindow: window !== null ? String(window) : '',
    autoCompactTokenLimit: window !== null ? String(Math.min(200_000, Math.floor(window * 0.8))) : '',
    efforts,
    defaultEffort: efforts.length > 0 ? 'medium' : '',
    verified: false,
  };
}

/** BoardFlight's id for a provider's model: the part after the slash, in the id's alphabet. */
function idFrom(providerId: string): string {
  return (providerId.split('/').pop() ?? providerId)
    .toLowerCase()
    .replace(/[^a-z0-9.-]+/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, 64);
}

/** OpenRouter names a model `OpenAI: GPT-6.1 Sol`; the list says `GPT-6.1 Sol`. */
function labelFrom(item: AdminProviderModel): string {
  const name = item.name?.includes(': ') ? item.name.slice(item.name.indexOf(': ') + 2) : item.name;
  return (name ?? item.id.split('/').pop() ?? item.id).slice(0, 64);
}

function Editor({ draft: initial, onClose, onSaved }: { draft: Draft; onClose: () => void; onSaved: () => void }) {
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const change = (next: Partial<Draft>) => setDraft((current) => ({ ...current, ...next }));

  function toggleEffort(effort: TeamAiEffort, on: boolean) {
    const efforts = TeamAiEffort.options.filter((one) => (one === effort ? on : draft.efforts.includes(one)));
    const defaultEffort = draft.defaultEffort !== '' && efforts.includes(draft.defaultEffort) ? draft.defaultEffort : (efforts[0] ?? '');
    change({ efforts, defaultEffort });
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const parsed = SetTeamAiModelRequest.safeParse({
      label: draft.label,
      ids: {
        openrouter: draft.openrouter.trim() || null,
        cheaperinference: draft.cheaperinference.trim() || null,
      },
      contextWindow: Number(draft.contextWindow),
      autoCompactTokenLimit: Number(draft.autoCompactTokenLimit),
      efforts: draft.efforts,
      defaultEffort: draft.defaultEffort || null,
      verified: draft.verified,
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(issue ? `${issue.path.join('.') || 'model'}: ${issue.message}` : 'Check the fields.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await put(`/v1/admin/ai-models/${encodeURIComponent(draft.id)}`, parsed.data);
      onSaved();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={draft.existing ? `Edit ${initial.label}` : 'Add a model'} onClose={onClose}>
      <form className="form-grid" onSubmit={(event) => void save(event)}>
        <label>
          <span className="muted small">Id</span>
          <input
            type="text"
            value={draft.id}
            disabled={draft.existing}
            onChange={(event) => change({ id: event.target.value })}
            spellCheck={false}
          />
          <span className="note">Never changes once teams use it: their switched-off lists name it.</span>
        </label>
        <label>
          <span className="muted small">Name</span>
          <input type="text" value={draft.label} onChange={(event) => change({ label: event.target.value })} />
        </label>
        <label>
          <span className="muted small">OpenRouter id</span>
          <input
            type="text"
            value={draft.openrouter}
            onChange={(event) => change({ openrouter: event.target.value })}
            placeholder="Not served there"
            spellCheck={false}
          />
        </label>
        <label>
          <span className="muted small">Cheaper Inference id</span>
          <input
            type="text"
            value={draft.cheaperinference}
            onChange={(event) => change({ cheaperinference: event.target.value })}
            placeholder="Not served there"
            spellCheck={false}
          />
        </label>
        <label>
          <span className="muted small">Context window (tokens)</span>
          <input
            type="number"
            min={8000}
            value={draft.contextWindow}
            onChange={(event) => change({ contextWindow: event.target.value })}
          />
          <span className="note">Codex uses at most 272k of it, whatever the model takes.</span>
        </label>
        <label>
          <span className="muted small">Compacts at (tokens)</span>
          <input
            type="number"
            min={8000}
            value={draft.autoCompactTokenLimit}
            onChange={(event) => change({ autoCompactTokenLimit: event.target.value })}
          />
          <span className="note">The whole context is sent on every request, so this is what a turn costs at most.</span>
        </label>
        <div className="wide">
          <span className="muted small">Reasoning efforts</span>
          <div className="checks">
            {TeamAiEffort.options.map((effort) => (
              <label key={effort} className="row">
                <input
                  type="checkbox"
                  checked={draft.efforts.includes(effort)}
                  onChange={(event) => toggleEffort(effort, event.target.checked)}
                />
                {effort}
              </label>
            ))}
            <label className="row">
              <span className="muted small">default</span>
              <select
                value={draft.defaultEffort}
                disabled={draft.efforts.length === 0}
                onChange={(event) => change({ defaultEffort: event.target.value as TeamAiEffort })}
              >
                {draft.efforts.length === 0 ? <option value="">none</option> : null}
                {draft.efforts.map((effort) => (
                  <option key={effort} value={effort}>
                    {effort}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <label className="wide row">
          <input type="checkbox" checked={draft.verified} onChange={(event) => change({ verified: event.target.checked })} />
          Verified: seen driving Codex on a playground project
        </label>
        {error ? <p className="error wide">{error}</p> : null}
        <div className="wide row">
          <button className="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : draft.existing ? 'Save' : 'Add to the list'}
          </button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------------------------------------------------------------------- words */

/** `1.05M`, `262k`. */
function tokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (count >= 1_000) return `${Math.round(count / 1_000)}k`;
  return String(count);
}

function teams(count: number): string {
  return count === 1 ? '1 team' : `${count} teams`;
}
